import type { HeroId, Point } from './world-types.js';

/** The latest outcome of a vote, so every client can announce why a rest did or did not happen. */
export interface RestNotice { id: number; text: string }
export type RestKind = 'catch-breath' | 'make-camp' | 'safe-rest' | 'wait';
/** How long a Wait lasts: whole hours, or until the next dawn. */
export type WaitLength = number | 'dawn';
/** What one hero spends. Hit Dice apply to Field Rests; the kit recovers a Wound during one. Safe Rest ignores both. */
export interface RestChoice { hitDice: number; medicalKit: boolean }
export interface RestDefinition { kind: RestKind; name: string; minutes: number; description: string; needsSafePlace: boolean }
export interface SafePlaceDefinition extends Point { id: string; name: string; radius: number }
export interface RestVoter { slot: string; playerName: string | null; status: 'pending' | 'accepted' }
/** The open vote, as every player sees it. Choices are shown so the party knows what is being spent. */
export interface RestProposal { id: number; kind: RestKind; proposer: string; remaining: number; voters: RestVoter[]; choices: Record<HeroId, RestChoice>; wait?: WaitLength }

// Core Rules pp. 16–17.
export const REST_DEFINITIONS: Record<RestKind, RestDefinition> = {
  'catch-breath': { kind: 'catch-breath', name: 'Catch Breath', minutes: 10, needsSafePlace: false,
    description: 'About 10 minutes. Each hero spends Hit Dice, rolling each and adding STR, to regain HP. A Medical Kit recovers a Wound.' },
  'make-camp': { kind: 'make-camp', name: 'Make Camp', minutes: 480, needsSafePlace: false,
    description: '8 hours with food and sleep. Spent Hit Dice take their maximum value plus STR. A Medical Kit recovers a Wound. Camping in the open may draw an ambush.' },
  'safe-rest': { kind: 'safe-rest', name: 'Safe Rest', minutes: 480, needsSafePlace: true,
    description: 'Overnight in a safe place. Restores all HP and Hit Dice and recovers 1 Wound. Temporary HP fades.' },
  // A video-game addition: passing time on watch, with no recovery. `minutes` is only the default length.
  wait: { kind: 'wait', name: 'Wait', minutes: 60, needsSafePlace: false,
    description: 'Pass the hours on watch. Nobody recovers anything. Waiting 8 hours or more in the open may draw an ambush.' },
};
export const REST_KINDS = Object.keys(REST_DEFINITIONS) as RestKind[];
/** Seconds the party has to answer before the vote lapses. */
export const REST_VOTE_SECONDS = 30;
/** Every resting hero must be within this distance of every other (meters, centre to centre). */
export const REST_GATHER_RADIUS = 30;
/** A living enemy this close to a resting hero (base edge, meters) makes resting unsafe. */
export const REST_ENEMY_DISTANCE = 25;
/** Chance an open-air Make Camp is interrupted: d20 at or under this value. */
export const AMBUSH_ROLL_MAX = 5;
/** The nearest uncleared enemy group within this distance of the camp (meters) may find it. */
export const AMBUSH_RANGE = 150;
/** Clock minutes that pass per second of unpaused game time: one in-game hour takes five real minutes. */
export const CLOCK_MINUTES_PER_SECOND = 60 / (5 * 60);
export const CLOCK_START_MINUTES = 8 * 60;
/** A Wait lasts 1 to this many whole hours. */
export const WAIT_MAX_HOURS = 24;
/** The clock time a Wait "until dawn" ends at, in minutes after midnight: first light, as scenarios' `dawn`. */
export const DAWN_MINUTES = 6.5 * 60;
/** A Wait this long or longer in the open may be ambushed, as Make Camp may. */
export const WAIT_AMBUSH_MINUTES = 8 * 60;
export const isWaitLength = (value: unknown): value is WaitLength => value === 'dawn' || Number.isInteger(value) && (value as number) >= 1 && (value as number) <= WAIT_MAX_HOURS;
/** Clock minutes a Wait of `length` lasts when it starts at `clock`. Until dawn ends exactly at dawn; at dawn itself it waits a whole day. */
export function waitMinutes(length: WaitLength, clock: number): number {
  if (length !== 'dawn') return length * 60;
  return (DAWN_MINUTES - clock % 1440 + 1440) % 1440 || 1440;
}

/** "1 hour", "3 hours" or "until dawn". */
export const waitText = (length: WaitLength) => length === 'dawn' ? 'until dawn' : `${length} hour${length === 1 ? '' : 's'}`;

export function formatClock(totalMinutes: number): string {
  const minutes = Math.max(0, Math.floor(totalMinutes)), day = Math.floor(minutes / 1440) + 1, inDay = minutes % 1440;
  return `Day ${day} · ${String(Math.floor(inDay / 60)).padStart(2, '0')}:${String(inDay % 60).padStart(2, '0')}`;
}
/** A hero's recovery from one Hit Die: STR can lower it but never below zero (Core Rules p. 16). */
export const hitDieRecovery = (roll: number, strength: number) => Math.max(0, roll + strength);
export const isRestKind = (value: unknown): value is RestKind => typeof value === 'string' && value in REST_DEFINITIONS;
export function emptyChoice(): RestChoice { return { hitDice: 0, medicalKit: false }; }
